export interface GeocodeAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface GeocodeResult {
  address_components?: GeocodeAddressComponent[];
  formatted_address?: string;
}

const LOCATION_TYPE_PRIORITY = [
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "sublocality_level_1",
  "sublocality",
  "neighborhood",
  "administrative_area_level_2",
] as const;

export function extractCityStateFromGeocodeResults(results: GeocodeResult[] | undefined | null) {
  let state = "";

  for (const type of LOCATION_TYPE_PRIORITY) {
    for (const result of results || []) {
      for (const component of result.address_components || []) {
        if (!state && component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }

        if (component.types.includes(type)) {
          return {
            city: component.long_name,
            state,
          };
        }
      }
    }
  }

  for (const result of results || []) {
    for (const component of result.address_components || []) {
      if (!state && component.types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
    }

    const fallbackLabel = result.formatted_address?.split(",")[0]?.trim();
    if (fallbackLabel) {
      return {
        city: fallbackLabel,
        state,
      };
    }
  }

  return {
    city: "",
    state,
  };
}

export function getGeolocationErrorMessage(error: unknown) {
  if (typeof GeolocationPositionError !== "undefined" && error instanceof GeolocationPositionError) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return {
          title: "Location access denied",
          description: "Please allow location access in your browser settings, or search for your city instead.",
        };
      case error.POSITION_UNAVAILABLE:
        return {
          title: "Location unavailable",
          description: "We couldn't read your current position. Try again or search for your city instead.",
        };
      case error.TIMEOUT:
        return {
          title: "Location timed out",
          description: "Your location took too long to load. Try again or search for your city instead.",
        };
      default:
        return {
          title: "Couldn't detect location",
          description: "Please search for your city instead.",
        };
    }
  }

  const message = error instanceof Error ? error.message : "";

  if (message.toLowerCase().includes("determine city")) {
    return {
      title: "Couldn't match your exact city",
      description: "We got your coordinates, but couldn't map them to a city name. Try searching for your city instead.",
    };
  }

  return {
    title: "Couldn't detect location",
    description: message || "Please search for your city instead.",
  };
}
